import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import { UserProvider, useUser } from './context';
import AccountPage from './pages/AccountPage';
import AccountProfileSection from './pages/AccountProfileSection';
import AccountSchoolProfileSection from './pages/AccountSchoolProfileSection';
import AccountStudentsSection from './pages/AccountStudentsSection';
import AccountCheckInCheckOutSection from './pages/AccountCheckInCheckOutSection';
import AccountBillingSection from './pages/AccountBillingSection';
import AccountSupportSection from './pages/AccountSupportSection';
import AttendancePage from './pages/AttendancePage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import {
  ACCOUNT_BASE_PATH,
  ACCOUNT_SCHOOL_PROFILE_PATH,
  OWNER_ONLY_ACCOUNT_SECTION_IDS,
  type AccountSectionId,
} from './pages/accountPaths';
import { SocialLoginCard } from './SocialLoginCard';
import UserAccountMenu from './UserAccountMenu';
import { UserCard } from './UserCard';

const testimonials = [
  {
    quote:
      'Our mornings are calmer now—kids tap in at the door and we actually see who is here. Big smiles all around.',
    author: 'Ms. Rivera',
    role: '2nd grade teacher',
  },
  {
    quote:
      'I love getting a little “all set!” ping on my phone. It feels friendly, not stressful, like the school is waving hello.',
    author: 'Jordan K.',
    role: 'Parent volunteer',
  },
  {
    quote:
      'The office team spends less time on clipboards and more time greeting families. That is the vibe we wanted.',
    author: 'Principal Chen',
    role: 'Lincoln Elementary',
  },
];

const LogoMark = () => {
  return (
    <div className="flex items-center gap-3" aria-label="OnTime Check-in logo">
      <svg
        className="h-14 w-14 shrink-0 text-ink"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <circle
          cx="32"
          cy="32"
          r="22"
          stroke="currentColor"
          strokeWidth="5"
          fill="#FFF9EC"
        />
        <path
          d="M22 33l7 7 13-16"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="text-left">
        <p className="font-display text-2xl font-semibold tracking-wide text-ink">
          OnTime
        </p>
        <p className="font-sans text-sm font-medium text-ink/70">
          School check-in
        </p>
      </div>
    </div>
  );
};

const TestimonialsRotator = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % testimonials.length),
      4800
    );
    return () => window.clearInterval(id);
  }, []);

  return (
    <section
      className="relative overflow-hidden rounded-3xl border-[3px] border-ink/15 bg-white/90 p-8 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
      aria-labelledby="testimonials-heading"
    >
      <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-skysoft/60 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full bg-grass/50 blur-2xl" />

      <h2
        id="testimonials-heading"
        className="font-display text-xl font-semibold text-ink"
      >
        Happy hallways say…
      </h2>
      <p className="mt-1 font-sans text-sm text-ink/60">
        Gentle rotating notes from our school community.
      </p>

      <div className="relative mt-8 min-h-[140px]">
        {testimonials.map((t, i) => (
          <blockquote
            key={t.author}
            className={`absolute inset-0 transition-all duration-700 ease-out ${
              i === active
                ? 'translate-y-0 opacity-100'
                : 'pointer-events-none translate-y-2 opacity-0'
            }`}
            aria-hidden={i !== active}
          >
            <p className="font-sans text-lg leading-relaxed text-ink/90">
              “{t.quote}”
            </p>
            <footer className="mt-4 flex flex-wrap items-baseline gap-2 font-sans text-sm">
              <cite className="not-italic font-semibold text-ink">{t.author}</cite>
              <span className="text-ink/50">· {t.role}</span>
            </footer>
          </blockquote>
        ))}
      </div>

      <div className="mt-10 flex gap-2" role="tablist" aria-label="Testimonial slides">
        {testimonials.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`h-2.5 rounded-full transition-all ${
              i === active ? 'w-8 bg-doghouse' : 'w-2.5 bg-ink/20 hover:bg-ink/35'
            }`}
            onClick={() => setActive(i)}
          />
        ))}
      </div>
    </section>
  );
};

const navLinkClassName =
  'rounded-full px-4 py-2 font-display text-sm font-medium text-ink/80 ring-2 ring-transparent transition hover:bg-skysoft/40 hover:text-ink focus-visible:outline-none focus-visible:ring-ink/30';

const Header = () => {
  const { user, status } = useUser();
  const showAccountMenu = status === 'ready' && user !== null;

  return (
    <header className="relative z-50 border-b-[3px] border-ink/10 bg-white/75 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <Link
          to="/"
          className="block shrink-0 rounded-xl outline-none ring-offset-2 ring-offset-white focus-visible:ring-2 focus-visible:ring-ink/35"
        >
          <LogoMark />
        </Link>
        <div className="flex flex-wrap items-center gap-3 sm:justify-end sm:gap-4">
          <nav
            className="flex flex-wrap items-center gap-2 sm:gap-4"
            aria-label="Primary"
          >
            <Link
              to="/"
              className={navLinkClassName}
            >
              Home
            </Link>
            <Link
              to="/how-it-works"
              className={navLinkClassName}
            >
              How it works
            </Link>
            <Link
              to="/pricing"
              className={navLinkClassName}
            >
              Pricing
            </Link>
            {!showAccountMenu ? (
              <Link to="/login" className={navLinkClassName}>
                Login
              </Link>
            ) : (
              <Link to={ACCOUNT_BASE_PATH} className={navLinkClassName}>
                Account
              </Link>
            )}
          </nav>
          {showAccountMenu && <UserAccountMenu />}
        </div>
      </div>
    </header>
  );
};

const HomePage = () => {
  const { user, status } = useUser();
  const showUserCard = status === 'ready' && user !== null;

  return (
    <main id="home" className="relative z-0 mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="mb-10 max-w-2xl">
        <p className="inline-flex rounded-full bg-skysoft/50 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-ink/70">
          School check-in, made gentle
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">
          A little cloud of calm for busy mornings
        </h1>
        <p className="mt-4 font-sans text-lg text-ink/70">
          OnTime helps families and staff check in with a smile—simple flows,
          soft colors, and room to breathe before the bell rings.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-8">
          <TestimonialsRotator />
          <section id="about" className="mt-8 rounded-3xl border-2 border-dashed border-ink/15 bg-white/40 p-6">
            <h2 className="font-display text-lg font-semibold text-ink">About</h2>
            <p className="mt-2 font-sans text-sm leading-relaxed text-ink/70">
              We are building a check-in experience that feels like a sunny comic
              strip—clear lines, warm paper, and friendly pacing—so every arrival
              feels welcomed, not rushed.
            </p>
            <Link
              to="/how-it-works"
              className="mt-4 inline-flex rounded-full bg-skysoft/70 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft"
            >
              See how it works
            </Link>
          </section>
        </div>
        <div className="lg:col-span-4">
          {showUserCard ? <UserCard /> : <SocialLoginCard />}
        </div>
      </div>
    </main>
  );
};

const OwnerOnlyAccountSection = ({
  sectionId,
  children,
}: {
  sectionId: AccountSectionId;
  children: React.ReactNode;
}) => {
  const { user, status } = useUser();
  if (status === 'loading') {
    return (
      <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
        <p className="font-sans text-sm text-ink/60">Loading…</p>
      </section>
    );
  }
  if (
    user?.schoolRole === 'staff' &&
    OWNER_ONLY_ACCOUNT_SECTION_IDS.includes(sectionId)
  ) {
    return <Navigate to={ACCOUNT_SCHOOL_PROFILE_PATH} replace />;
  }
  return <>{children}</>;
};

const AccountRoute = () => {
  const { user, status } = useUser();

  if (status !== 'ready') {
    return (
      <div className="relative z-10 flex min-h-[50vh] items-center justify-center font-sans text-sm text-ink/60">
        Loading…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AccountPage />;
};

const AppShell = () => {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-paper font-sans text-ink">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(201,228,245,0.55),transparent_45%),radial-gradient(circle_at_85%_10%,rgba(184,217,168,0.35),transparent_40%),radial-gradient(circle_at_50%_100%,rgba(232,165,152,0.2),transparent_50%)]" />

      <Header />

      <Outlet />

      <footer className="relative z-10 mt-12 border-t border-ink/10 py-8 text-center font-sans text-xs text-ink/45">
        © {new Date().getFullYear()} OnTime Check-in · Made for schools
      </footer>
    </div>
  );
};

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<HomePage />} />
          <Route path="how-it-works" element={<HowItWorksPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="attendance/:schoolId" element={<AttendancePage />} />
          <Route path="account" element={<AccountRoute />}>
            <Route index element={<Navigate to="user_profile" replace />} />
            <Route path="user_profile" element={<AccountProfileSection />} />
            <Route path="school_profile" element={<AccountSchoolProfileSection />} />
            <Route
              path="students"
              element={
                <OwnerOnlyAccountSection sectionId="students">
                  <AccountStudentsSection />
                </OwnerOnlyAccountSection>
              }
            />
            <Route path="check_in_check_out" element={<AccountCheckInCheckOutSection />} />
            <Route
              path="billing"
              element={
                <OwnerOnlyAccountSection sectionId="billing">
                  <AccountBillingSection />
                </OwnerOnlyAccountSection>
              }
            />
            <Route path="support" element={<AccountSupportSection />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <UserProvider>
      <AppRoutes />
    </UserProvider>
  );
};

export default App;
